import {api} from "@/shared/api";
import {InvoiceDataTable} from "./invoiceTable";
import {FreeModuleSelectionDialog} from "./freeModuleSelectionDialog";
import {TrialFeatureModuleSelectionDialog} from "@/features/pricing/trialFeatureModuleSelectionDialog";
import {FeatureModuleSelectionDialog} from "@/features/pricing/featureModuleSelectionDialog";
import {ResetTrialDataDialog} from "@/features/trial/resetTrialDataDialog";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Button from "@/shared/utils/button";
import * as Input from "@/shared/utils/input";
import {Notify} from "@/shared/ui/notify";
import {formatDate, isSameOrAfterDay, parseISODate, Temporal} from "@/shared/utils/datefunctions";
import {
	FeatureModule,
	FeatureModuleConfigurationResponse,
	FreeFeatureModuleResult,
	SaveBillingSettingsResponse,
	SaveTrialFeatureModulesResponse
} from "@/shared/timr";

declare let existingPaymentsPendingOverdue: boolean;

export class AccountAdministrationPage {

	constructor() {
		this.init();
	}

	private saveAccountSettingsAndValidateVAT(successCallback: () => void, failureCallback?: () => void) {
		if (!existingPaymentsPendingOverdue) {
			const saveAccountSettings = () => {
				this.saveBillingSettings(false, successCallback, failureCallback);
			};
			if ($("#vatEnabled").prop("checked")) {
				this.validateVAT(saveAccountSettings);
			} else {
				saveAccountSettings();
			}
		} else {
			BootstrapModal.error({message: "error.payment.due"});
		}
	}

	private saveBillingSettings(showMessage?: boolean, successCallback?: (data: SaveBillingSettingsResponse) => void, failureCallback?: () => void) {
		void api("account/saveBillingSettings.json")
			.postForm("#accountSettingsForm")
			.done((data: SaveBillingSettingsResponse) => {
				if (data.vatNumber) {
					$("#vatNumber").val(data.vatNumber);
				}
				this.configureVATFeedback(data.vatValid);
				if (showMessage) {
					Notify.success(messages["admin.clientSettings.saved"]);
				}
				if (successCallback) {
					successCallback(data);
				}
			})
			.fail(() => {
				if (failureCallback) {
					failureCallback();
				}
			});
	}

	private saveInvoiceEmailForm(showMessage?: boolean, successCallback?: (data: void) => void) {
		void api("account/saveInvoiceEmailSettings.json")
			.postForm("#invoiceEmailForm")
			.done(function (data) {
				if (showMessage) {
					Notify.success(messages["admin.clientSettings.saved"]);
				}
				if (successCallback) {
					successCallback(data);
				}
			});
	}

	private attemptSaveAccountOwner() {
		BootstrapModal.confirm({
			title: "admin.account.changeAccountOwner.dialogTitle",
			message: "admin.account.changeAccountOwner.question",
			yesLabel: "admin.account.changeAccountOwner.yesLabel",
			noLabel: "dialog.cancel",
			yesAction: function (modal) {
				modal.close();

				void api("admin/saveAccountOwner.json")
					.postForm("#accountOwnerForm")
					.done(function () {
						Notify.success(messages["admin.clientSettings.saved"]);
					});
			}
		});
	}

	private validateVAT(success?: () => void) {
		void api("account/validateVAT.json")
			.postForm("#accountSettingsForm")
			.done(data => {
				if (data.vatNumber) {
					$("#vatNumber").val(data.vatNumber);
				}
				this.configureVATFeedback(data.valid);
				if (data.valid === true) {
					if (success) {
						success();
					} else {
						Notify.success(messages["admin.account.validateVAT.valid"]);
					}
				} else if (data.valid === false) {
					BootstrapModal.error({
						title: "admin.account.validateVAT.title",
						message: "admin.account.validateVAT.invalid"
					});
				} else { // undefined
					BootstrapModal.error({
						title: "admin.account.validateVAT.title",
						message: "admin.account.validateVAT.unknown"
					});
				}
			})
			.fail(function () {
				Input.addFeedback("#vatNumber", "has-warning");
			});
	}

	private configureVATFeedback(valid: boolean | undefined) {
		if (valid === true) {
			Input.addFeedback("#vatNumber", "has-success");
		} else if (valid === false) {
			Input.addFeedback("#vatNumber", "has-error");
		} else { // undefined
			Input.addFeedback("#vatNumber", "has-warning");
		}
	}

	private configureVATFields() {
		if ($("#country").find("option:selected").data("ineu")) {
			$("#vatFields").show();
			if ($("#vatNumber").val()) {
				$("#vatEnabled").prop("checked", true);
				$("#vatTable").show();
			}
		} else {
			$("#vatFields").hide();
			$("#vatEnabled").prop("checked", false);
			$("#vatNumber").val("");
		}
	}

	private onCountryChanged() {
		this.configureVATFields();
		const selectedOption = $("#country").find("option:selected");
		$("#countryCode").html(selectedOption.data("code") as string);
	}

	private createInvoiceTable() {
		return new InvoiceDataTable({
			element: $("#invoiceTable")
		});
	}

	private initActiveFeatureModules() {
		void api("account/getFeatureModuleConfigurationForAccount.json")
			.get()
			.done((data: FeatureModuleConfigurationResponse) => {
				const moduleList = $("#featureModuleList");
				$.each(data?.activeFeatureModules, (idx, module: FeatureModule) => {
					moduleList.append(
						$("<li>").text(`${module.name} ${module.trialUntil ? `(${messages["admin.account.trialUntil"]} ${formatDate(parseISODate(module.trialUntil))})` : ""}`)
					);
				});

				const selectFeatureModulesButton = $("#selectFeatureModulesButton");
				const trialMoreModulesButton = $("#trialMoreModulesButton");
				$("#licensedUsersCount").text(data?.licensedUsers || "-");
				$("#currentTermValidTo").text(data?.validTo && !data?.free ? formatDate(parseISODate(data?.validTo)) : "-");
				$("#activeUntilHeader").text(data?.trial ? messages["admin.account.activeUntil.trial"] : messages["admin.account.activeUntil"]);
				$("#activeUserLicencesContainer").toggle(!data?.trial);
				selectFeatureModulesButton.toggle(data?.trial && isSameOrAfterDay(parseISODate(data?.validTo), Temporal.Now.plainDateISO()));
				trialMoreModulesButton.toggle(data?.trialAllowed);
				$("#trialHeader").toggle(data?.trial);
				$("#customizeSubscriptionBtn")
					.text(data?.trial ?
						messages["admin.account.buyTimrNow"] :
						messages["admin.account.upgradeButton"]
					)
					.prop("disabled", data?.activeSubscription)
					.click(() => this.saveAccountSettingsAndValidateVAT(() => window.open("/timr/pricing/customize.html", "_self")));
				selectFeatureModulesButton.click(e => {
					const btn = $(e.target);
					Button.setLoading(btn);
					this.loadAndOpenFeatureModuleSelectionDialog(data, btn);
				});
				trialMoreModulesButton.click(e => {
					const btn = $(e.target);
					Button.setLoading(btn);
					this.loadAndOpenTrialFeatureModuleSelectionDialog(data, btn);
				});
				$("#switchToFreeBtn").prop("disabled", data?.activeSubscription);
				$("#activeSubscriptionInfo")
					.toggle(data?.activeSubscription)
					.text(data?.activeSubscription ? messages.withArguments("admin.account.changePlanNotAllowedForSubscriptions", data?.activeSubscriptionVendor) : "");
			});

		$("#switchToFreeBtn").click(() => {
			void api("pricing/getFreeFeatureModules.json")
				.get()
				.done((response: FreeFeatureModuleResult[]) => {
					FreeModuleSelectionDialog.show(response, () => window.location.reload());
				});
		});
	}

	private onSaveTrialFeatureModules(this: void, selectedModules: FeatureModule[]) {
		void api("pricing/saveTrialFeatureModules.json")
			.post({selectedModuleIds: selectedModules.map(fm => fm.id)})
			.done((response: SaveTrialFeatureModulesResponse) => {
				if (response.reLoginRequired) {
					window.location.href = "/timr/reLoginRequired.html";
				} else {
					window.location.reload();
				}
			});
	}

	private loadAndOpenFeatureModuleSelectionDialog(data: FeatureModuleConfigurationResponse, btn: JQuery) {
		api("pricing/getAvailableFeatureModules.json")
			.get()
			.done(config => {
				FeatureModuleSelectionDialog.show(
					{
						baseFeatureModules: config.baseFeatureModules,
						extensionFeatureModules: config.extensionFeatureModules,
						activeFeatureModules: data.activeFeatureModules,
						allFeatureModulesInConfiguration: data.allFeatureModulesInConfiguration,
						userCount: data.licensedUsers
					},
					this.onSaveTrialFeatureModules
				);
			})
			.always(() => Button.resetLoading(btn));
	}

	private loadAndOpenTrialFeatureModuleSelectionDialog(data: FeatureModuleConfigurationResponse, btn: JQuery) {
		api("pricing/getAvailableFeatureModulesForTrial.json")
			.get()
			.done(config => {
				TrialFeatureModuleSelectionDialog.show(
					{
						baseFeatureModules: config.baseFeatureModules,
						extensionFeatureModules: config.extensionFeatureModules,
						activeFeatureModules: data.activeFeatureModules,
						allFeatureModulesInConfiguration: data.allFeatureModulesInConfiguration,
						userCount: data.licensedUsers,
					}
				);
			})
			.always(() => Button.resetLoading(btn));
	}

	private init() {
		$("#validateVAT").click(() => {
			this.validateVAT();
			return false;
		});

		$("#country").change(() => {
			this.onCountryChanged();
		});

		$("#vatNumber").change(() => {
			Input.addFeedback("#vatNumber", "has-warning");
		});

		$("#accountSettingsForm").submit(event => {
			event.preventDefault();
			this.saveBillingSettings(true);
		});

		$("#invoiceEmailForm").submit(event => {
			event.preventDefault();
			this.saveInvoiceEmailForm(true);
		});


		$("#accountOwnerForm").submit(event => {
			event.preventDefault();
			this.attemptSaveAccountOwner();
		});

		$("#vatEnabled").click(event => {
			const isChecked = $(event.target).is(":checked");
			$("#vatTable").toggle(isChecked);
			if (!isChecked) {
				$("#vatNumber").val("");
			}
		});

		this.onCountryChanged();

		this.createInvoiceTable();

		this.initActiveFeatureModules();

		$("[data-trial-data-reset]").on("click", e => {
			Button.setLoading(e.target);
			ResetTrialDataDialog.show()
				.always(() => Button.resetLoading(e.target));
		});
	}
}
